import { TConvertKeys, TNullable } from './common';
import {
  IUploadDocumentData,
  IUploadDocumentDataRequest,
  TDocumentUserType,
} from './documents';
import { TUserSummary } from './user';

export type TFormBuilderContent = 'visitNote' | 'formBuilder';
export type TFormBuilderPreviewModeSize = 'mobile' | 'desktop';

export enum ESignatureSettingsType {
  NO_SIGNATURE = 'no_signature',
  TYPED = 'typed',
  DRAW_AND_TYPED = 'draw_and_typed',
}

export enum ESignatureSettingsRequiredBy {
  PROVIDER = 'provider',
  PATIENT = 'patient',
  PATIENT_AND_PROVIDER = 'patient_and_provider',
}

export enum EFormBuilderFieldType {
  TEXT = 'text', // answer = undefined
  CHECKBOX = 'checkbox', // [option1]
  SINGLE_LINE_INPUT = 'single_line_input', // answer = string
  MULTILINE_INPUT = 'multiline_input', // answer = string
  SINGLE_CHOICE = 'single_choice', // answer = [option1]
  MULTIPLE_CHOICE = 'multiple_choice', // answer = [option1, ...]
  DROPDOWN = 'dropdown', // answer = [option1, ...]
  DATE = 'date', // answer = string
  FILE = 'file', // answer = IUploadDocumentData[]
  TEXT_EDITOR = 'text_editor',
}

export type TFormBuilderOption = {
  id: string;
  value: string;
};

type TFormBuilderBaseField = {
  id: string;
};

export type TFormBuilderTextField = {
  type: EFormBuilderFieldType.TEXT;
  text: string;
} & TFormBuilderBaseField;

export type TFormBuilderCheckboxField = {
  type: EFormBuilderFieldType.CHECKBOX;
  label: string;
  options: TFormBuilderOption[];
  required?: boolean;
} & TFormBuilderBaseField;

export type TFormBuilderSingleLineInputField = {
  type: EFormBuilderFieldType.SINGLE_LINE_INPUT;
  label: string;
  placeholder: string;
  required?: boolean;
} & TFormBuilderBaseField;

export type TFormBuilderMultilineInputField = {
  type: EFormBuilderFieldType.MULTILINE_INPUT;
  label: string;
  placeholder: string;
  required?: boolean;
} & TFormBuilderBaseField;

export type TFormBuilderTextEditorField = {
  type: EFormBuilderFieldType.TEXT_EDITOR;
  label: string;
  placeholder: string;
  required?: boolean;
} & TFormBuilderBaseField;

export type TFormBuilderSingleChoiceField = {
  type: EFormBuilderFieldType.SINGLE_CHOICE;
  label: string;
  options: TFormBuilderOption[];
  required?: boolean;
} & TFormBuilderBaseField;

export type TFormBuilderMultipleChoiceField = {
  type: EFormBuilderFieldType.MULTIPLE_CHOICE;
  label: string;
  options: TFormBuilderOption[];
  required?: boolean;
} & TFormBuilderBaseField;

export type TFormBuilderDropdownField = {
  type: EFormBuilderFieldType.DROPDOWN;
  label: string;
  placeholder: string;
  options: TFormBuilderOption[];
  required?: boolean;
} & TFormBuilderBaseField;

export type TFormBuilderDateField = {
  type: EFormBuilderFieldType.DATE;
  label: string;
  required?: boolean;
} & TFormBuilderBaseField;

export type TFormBuilderFileField = {
  type: EFormBuilderFieldType.FILE;
  label: string;
  required?: boolean;
} & TFormBuilderBaseField;

export type TFormBuilderField =
  | TFormBuilderCheckboxField
  | TFormBuilderSingleLineInputField
  | TFormBuilderMultilineInputField
  | TFormBuilderTextEditorField
  | TFormBuilderSingleChoiceField
  | TFormBuilderMultipleChoiceField
  | TFormBuilderDropdownField
  | TFormBuilderDateField
  | TFormBuilderFileField
  | TFormBuilderTextField;

export type TSignatureSettings = {
  type: ESignatureSettingsType;
  required_by: ESignatureSettingsRequiredBy;
};

type TFormBuilderSchemaCommon = {
  title: string;
  fields: TFormBuilderField[];
};

type TFormBuilderEditSchema = TFormBuilderSchemaCommon & {
  signature_settings: TSignatureSettings;
};

type TFormBuilderTemplate<T> = {
  id: string;
  data: T;
  institution_id: string;
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;
  deleted_at?: string;
  user_type?: TDocumentUserType;
};

export type TFormBuilderEditTemplate =
  TFormBuilderTemplate<TFormBuilderEditSchema>;

export type TFormBuilderAnswerBase = string;

export type TFormBuilderCheckboxFieldAnswer = TFormBuilderCheckboxField & {
  answer: TNullable<TFormBuilderOption['id'][]>;
};
export type TFormBuilderSingleLineInputFieldAnswer =
  TFormBuilderSingleLineInputField & {
    answer: TNullable<TFormBuilderAnswerBase>;
  };
export type TFormBuilderMultilineInputFieldAnswer =
  TFormBuilderMultilineInputField & {
    answer: TNullable<TFormBuilderAnswerBase>;
  };

type TFormBuilderTextEditorFieldAnswer = TFormBuilderTextEditorField & {
  answer: TNullable<TFormBuilderAnswerBase>;
};

export type TFormBuilderSingleChoiceFieldAnswer =
  TFormBuilderSingleChoiceField & {
    answer: TNullable<TFormBuilderOption['id'][]>;
  };
export type TFormBuilderMultipleChoiceFieldAnswer =
  TFormBuilderMultipleChoiceField & {
    answer: TNullable<TFormBuilderOption['id'][]>;
  };
export type TFormBuilderDropdownFieldAnswer = TFormBuilderDropdownField & {
  answer: TNullable<TFormBuilderOption['id'][]>;
};
export type TFormBuilderDateFieldAnswer = TFormBuilderDateField & {
  answer: TNullable<TFormBuilderAnswerBase>;
};
type TFormBuilderFileFieldAnswer = TFormBuilderFileField & {
  answer: TNullable<IUploadDocumentData[]>;
};

export type TFormBuilderFileFieldAnswerCamel =
  TConvertKeys<TFormBuilderFileFieldAnswer>;

export type TFormBuilderEntryField =
  | TFormBuilderCheckboxFieldAnswer
  | TFormBuilderSingleLineInputFieldAnswer
  | TFormBuilderMultilineInputFieldAnswer
  | TFormBuilderTextEditorFieldAnswer
  | TFormBuilderSingleChoiceFieldAnswer
  | TFormBuilderMultipleChoiceFieldAnswer
  | TFormBuilderDropdownFieldAnswer
  | TFormBuilderDateFieldAnswer
  | TFormBuilderFileFieldAnswer
  | TFormBuilderTextField;

export type TFormBuilderEntryFieldCamel = TConvertKeys<TFormBuilderEntryField>;

export type TFormBuilderEntrySignature = {
  user_institution_role_id: string;
  created_at?: string;
  name: string;
  document?: IUploadDocumentDataRequest;
  meta?: {
    ip_address: TNullable<string>;
    user_agent: TNullable<string>;
  };
  user?: Partial<TUserSummary>;
};

export type TFormBuilderEntrySchema = Omit<TFormBuilderEditSchema, 'fields'> & {
  fields: TFormBuilderEntryField[];
};

export interface TFormBuilderEntry {
  id: string;
  user_institution_role_id: string;
  appointment_id: string;
  template_id: string;
  signatures: TFormBuilderEntrySignature[];
  data: TFormBuilderEntrySchema & { required?: boolean };
  created_at?: string;
  updated_at?: TNullable<string>;
  deleted_at?: TNullable<string>;
  form_template: TFormBuilderEditTemplate;
  meta?: {
    is_shared?: boolean;
  };
}

export type TFormBuilderEditSchemaCamel = TConvertKeys<TFormBuilderEditSchema>;
export type TFormBuilderEditTemplateCamel =
  TConvertKeys<TFormBuilderEditTemplate>;
export type TSignatureSettingsCamel = TConvertKeys<TSignatureSettings>;
export type TFormBuilderEntryCamel = TConvertKeys<TFormBuilderEntry>;
export type TFormBuilderEntrySignatureCamel =
  TConvertKeys<TFormBuilderEntrySignature>;

// REQUESTS
export type TPatchFormBuilderEntryRequest = {
  fields?: { id: string; answer: string | string[] }[];
  required?: boolean;
  meta?: {
    is_shared?: boolean;
  };
};

export type TPatchFormBuilderEntrySignatureRequest = {
  file?: File;
  name?: string;
};

export enum EDocumentManagementStatus {
  COMPLETED = 'completed',
  PROVIDER_TO_SIGN = 'provider-to-sign',
  NOT_COMPLETED = 'not-completed',
  SHARED = 'shared',
}

/**
 * Optional because templates saved before signatures existed carry no
 * settings; the builder seeds a default when it loads one.
 */
export type TVisitNotesSchema = TFormBuilderSchemaCommon & {
  signature_settings?: TSignatureSettings;
};
export type TVisitNotesTemplate = TFormBuilderTemplate<TVisitNotesSchema>;

export type TVisitNotesSchemaCamel = TConvertKeys<TVisitNotesSchema>;
export type TVisitNotesTemplateCamel = TConvertKeys<TVisitNotesTemplate>;

export type TFormBuilderEntryVariant = 'edit' | 'view';
